import {runInAction, observable, action} from 'mobx';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris';
import Organization from './models/Organization';
import DefaultLogo from '~/assets/images/logo_default.svg';
import Pagination from './Pagination';

export class OrganizationStore extends Pagination {
  searchText = '';

  @observable isLoading = false;

  @observable ou = {};

  @observable organizations;

  @observable domains = [];

  @observable pageFooter = {};

  @observable editPersonality = false;

  @observable organizationAdminInfo;

  @action('get ou list')
  getOrganizations(body = {}) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getOrganizationList(), {
        ...body
      })
      .then((data) => {
        this.setOrganizations(data.organizations.map(o => this.updateOuFromServer(o)));
        this.setPagination(data.pagination);
        runInAction(() => {
          this.isLoading = false;
        });
      });
  }

  @action.bound
  getOrganizationsWithAddition(body = {}) {
    let data = {
      search: this.searchText.trim(),
      ...this.paginationRequestBody
    };

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getOrganizations({
      ...data,
      ...body
    });
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
    this.getOrganizationsWithAddition();
  }

  @action
  getOUInfo() {
    axiosInstance.get(uris.getOrganizationInfo()).then((data) => {
      if (data && data.addition) {
        if (/^\//.test(data.addition.logo)) {
          data.addition.logo = `/app-portal${data.addition.logo}`;
        }
        data.addition.logo = data.addition.logo || DefaultLogo;
      }
      this.setCurrentOu(this.updateOuFromServer(data));
    });
  }

  @action
  setOrganizations(data) {
    this.organizations = data;
  }

  @action('set current ou')
  setCurrentOu(data) {
    this.ou = data;
  }

  @action
  updateOuFromServer(json) {
    const ou = new Organization(this, json.id);
    ou.updateFromJson(json);
    return ou;
  }

  @action
  setImage(formData) {
    return axiosInstance.post(uris.uploadOrganizationLogo(), formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    });
  }

  @action('set organization admin')
  setOrganizationAdmin(userId, organizationId) {
    return axiosInstance.post(uris.setOrganizationAdmin(), {
      organizationId,
      userId
    });
  }

  @action('create and set organization admin')
  createAndSetOrganizationAdmin({organizationId, name, email, domain}) {
    return axiosInstance.post(uris.createAndSetOrganizationAdmin(), {
      organizationId,
      name,
      email,
      domain
    });
  }

  @action.bound
  onSearch(value = '') {
    this.searchText = value;
    console.log(this.searchText);
    this.getOrganizationsWithAddition(this.initPaginationRequestBody);
  }

  @action.bound
  getDomainList(organizationId) {
    axiosInstance
      .get(uris.getDomainList(), {
        params: {organizationId}
      })
      .then((data) => {
        runInAction(() => {
          this.domains = data.domains || [];
        });
      });
  }

  @action.bound
  formatPageFooter(data) {
    if (data.zh === '' && data.en === '' && data.es === '' && data.ja === '') {
      this.pageFooter = {
        'zh-CN': '©Powered by EnOS',
        'en-US': '©Powered by EnOS',
        'es-ES': '©Con tecnología de EnOS',
        'ja-JP': '©EnOS提供'
      };
    } else {
      this.pageFooter = {
        'zh-CN': data.zh === 'undefined' ? '' : data.zh,
        'en-US': data.en === 'undefined' ? '' : data.en,
        'es-ES': data.es === 'undefined' ? '' : data.es,
        'ja-JP': data.ja === 'undefined' ? '' : data.ja
      };
    }
  }

  @action.bound
  setPageFooter(data) {
    if (data) {
      this.formatPageFooter(data);
    } else {
      this.pageFooter = data;
    }
  }

  @action.bound
  setEditPersonality(boolean) {
    this.editPersonality = boolean;
  }

  @action.bound
  getOrganizationCopyRight() {
    axiosInstance
      .get(uris.getOrganizationCopyRight())
      .then((data) => {
        this.setEditPersonality(data.available);
        this.setPageFooter(data.copyrights);
      });
  }

  @action.bound
  setOrganizationCopyRight(copyrights) {
    axiosInstance
      .post(uris.setOrganizationCopyRight(), {copyrights})
      .then(() => {
        this.getOrganizationCopyRight();
      });
  }

  @action.bound
  setOrganizationAdminInfo(data) {
    this.organizationAdminInfo = data;
  }

  @action.bound
  getOrganizationAdminInfo() {
    axiosInstance
      .get(uris.getOrganizationAdminInfo())
      .then((data) => {
        this.setOrganizationAdminInfo(data);
      });
  }
}

export default new OrganizationStore();
